// Your Figma access token: lets "paste from Figma" bring image pixels (Figma's clipboard only
// references images; the server fetches them from Figma's API with this token).
import { useEffect, useState } from 'react';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';

export function FigmaTokenDialog() {
  const open = useApp('ui', () => app.ui.figmaTokenOpen);
  const [status, setStatus] = useState<{ configured: boolean; source: 'yours' | 'server' | null } | null>(null);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setToken('');
    setError('');
    void fetch('/api/figma/token')
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [open]);
  if (!open) return null;
  const close = () => app.setUI({ figmaTokenOpen: false });
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await fetch('/api/figma/token', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token }) });
      const body = (await r.json()) as { handle?: string; error?: string };
      if (!r.ok) throw new Error(body.error ?? r.statusText);
      app.toast(`Figma token saved${body.handle ? ` for ${body.handle}` : ''}: pasted images now come with their pixels`);
      close();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    await fetch('/api/figma/token', { method: 'DELETE' });
    setStatus({ configured: false, source: null });
  };
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && close()}>
      <div className="modal" style={{ width: 'min(480px, calc(100vw - 32px))' }}>
        <header>
          <span>Figma access token</span>
          <button className="icon-btn" title="Close" aria-label="Close" onClick={close}>
            {Icons.close}
          </button>
        </header>
        <div className="body" style={{ display: 'grid', gap: 10 }}>
          <p style={{ margin: 0 }}>
            Pasting from Figma brings layers, text and vectors on its own. Images need your token: Figma&apos;s clipboard only names them, so Vellum downloads them from Figma with it. Create one in Figma under <b>Settings › Security › Personal access tokens</b> with read access to file content.
          </p>
          {status?.configured && <p style={{ margin: 0, color: 'var(--text-2)' }}>{status.source === 'server' ? 'This server has a shared token.' : 'You have a token saved.'} Saving a new one replaces yours.</p>}
          <input
            type="password"
            autoFocus
            autoComplete="off"
            placeholder="figd_…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => {
              // typing here must not trigger canvas shortcuts
              e.stopPropagation();
              if (e.key === 'Enter' && token) void save();
            }}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)' }}
          />
          {error && <div style={{ color: 'var(--danger, #f24822)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {status?.source === 'yours' && (
              <button className="btn secondary" onClick={() => void remove()}>
                Remove mine
              </button>
            )}
            <button className="btn" disabled={!token || busy} onClick={() => void save()}>
              {busy ? 'Checking…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
